import React, { useContext, useState, createContext, useCallback, useEffect, useMemo } from 'react'
import { dbSelect } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from './SessionContext'

interface LabelsContextType {
  getSpotTypeLabel: (spotTypeId: number) => string | undefined
  getAudioStatusLabel: (spotAudioStatusId: number) => string | undefined
  /**
   * The whole lookup, unfiltered on is_active: the activation-type filter
   * (05.3) derives its options from here, so a type added to the vocabulary
   * (e.g. `custom`, 06.3) appears by itself, without touching the filter.
   */
  spotTypes: Array<{ id: number; label: string }>
  loading: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars

const LabelsContext = createContext<LabelsContextType>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getSpotTypeLabel: (_spotTypeId: number) => undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAudioStatusLabel: (_spotAudioStatusId: number) => undefined,
  spotTypes: [],
  loading: true,
})

export const useLabelsContext = () => useContext(LabelsContext)

interface LabelsContextProviderProps {
  children: React.ReactNode
}

const LabelsContextProvider: React.FC<LabelsContextProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState(true)
  const { loading: sessionLoading } = useSessionContext()
  const [spotTypes, setSpotTypes] = useState<Array<{ id: number; label: string }> | null>(null)
  const [audioStatusTypes, setAudioStatusTypes] = useState<Array<{ id: number; label: string }> | null>(null)

  const fetchLabels = async () => {
    const types = await dbSelect({ table: 'spot_types' })

    if (types.error) {
    }

    if (!types.error && types.data) {
      setSpotTypes(types.data as Array<{ id: number; label: string }>)
    }

    const audioStatus = await dbSelect({ table: 'spot_audio_status' })

    if (audioStatus.error) {
    }

    if (!audioStatus.error && audioStatus.data) {
      setAudioStatusTypes(audioStatus.data as Array<{ id: number; label: string }>)
    }

    setLoading(false)
  }

  const getSpotTypeLabel = useCallback(
    (spotTypeId: number): string | undefined => {
      if (!spotTypeId || !spotTypes) return undefined
      const entry = spotTypes.find((spotType) => spotType.id === spotTypeId)
      return entry?.label
    },
    [spotTypes]
  )

  const getAudioStatusLabel = useCallback(
    (spotAudioStatusId: number): string | undefined => {
      if (!spotAudioStatusId || !audioStatusTypes) return undefined
      const entry = audioStatusTypes.find((audioStatus) => audioStatus.id === spotAudioStatusId)
      return entry?.label
    },
    [audioStatusTypes]
  )

  useEffect(() => {
    if (!sessionLoading) {
      fetchLabels()
    }
  }, [sessionLoading])

  const contextValue = useMemo(
    () => ({
      getSpotTypeLabel,
      getAudioStatusLabel,
      spotTypes: spotTypes ?? [],
      loading,
    }),
    [getSpotTypeLabel, getAudioStatusLabel, spotTypes, loading]
  )

  return <LabelsContext.Provider value={contextValue}>{children}</LabelsContext.Provider>
}

export default LabelsContextProvider
